import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import supabase from '../supabaseClient';
import './AuthCallback.css';

/**
 * AuthCallback Component
 *
 * Handles all email-based authentication callbacks:
 * - Email confirmation (signup)
 * - Magic link sign-in
 * - Password reset
 * - Email change confirmation
 * - Reauthentication
 *
 * This component is the landing page for all Supabase email links.
 */
function AuthCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Verifying your email...');
  const [errorDetails, setErrorDetails] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    handleAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthCallback = async () => {
    try {
      // Get the hash parameters (Supabase uses hash for auth tokens)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));

      // Get query parameters as well (sometimes used for error messages)
      const type = hashParams.get('type') || searchParams.get('type');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const error = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');
      const errorCode = hashParams.get('error_code');

      console.log('Auth callback triggered:', {
        type,
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        error,
        errorDescription,
        errorCode
      });

      // Handle errors from Supabase
      if (error) {
        setStatus('error');
        setMessage('Authentication failed');
        setErrorDetails(errorDescription || error);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      // If we have tokens, set the session
      if (accessToken && refreshToken) {
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (sessionError) {
          throw sessionError;
        }

        console.log('Session set successfully:', sessionData);

        // Handle different callback types
        switch (type) {
          case 'signup':
            // Email confirmation after signup
            setStatus('success');
            setMessage('Email confirmed! Welcome to IntelliTrain!');
            setTimeout(() => {
              navigate('/onboarding');
            }, 2000);
            break;

          case 'magiclink':
            // Magic link sign-in
            setStatus('success');
            setMessage('Signed in successfully!');
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
            break;

          case 'recovery':
            // Password reset
            setStatus('success');
            setMessage('Email verified! Please set your new password.');
            setTimeout(() => {
              navigate('/reset-password');
            }, 2000);
            break;

          case 'email_change':
            // Email change confirmation
            setStatus('success');
            setMessage('Email updated successfully!');
            setTimeout(() => {
              navigate('/profile');
            }, 2000);
            break;

          case 'invite':
            // User invitation accepted
            setStatus('success');
            setMessage('Invitation accepted! Setting up your account...');
            setTimeout(() => {
              navigate('/onboarding');
            }, 2000);
            break;

          default:
            // Generic success - redirect to dashboard
            setStatus('success');
            setMessage('Authentication successful!');
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
            break;
        }
      } else {
        // No tokens provided - might be an older link or error
        throw new Error('No authentication tokens received. The link may have expired.');
      }
    } catch (err) {
      console.error('Auth callback error:', err);
      setStatus('error');
      setMessage('Authentication failed');
      setErrorDetails(err.message || 'An unexpected error occurred');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  };

  return (
    <div className="auth-callback-container">
      <div className="auth-callback-box">
        {status === 'processing' && (
          <>
            <div className="spinner"></div>
            <h2>{message}</h2>
            <p className="auth-callback-subtitle">Please wait a moment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="success-icon">✓</div>
            <h2>{message}</h2>
            <p className="auth-callback-subtitle">Redirecting you now...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="error-icon">✕</div>
            <h2>{message}</h2>
            {errorDetails && (
              <p className="error-details">{errorDetails}</p>
            )}
            <p className="auth-callback-subtitle">Redirecting to login...</p>
            <button
              className="manual-redirect-button"
              onClick={() => navigate('/login')}
            >
              Go to Login Now
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
